import React from 'react';
import Form from 'react-bootstrap/Form';
import dateformat from 'dateformat';
import { orderTags } from '../../resources/orderResources';
import { useAuth } from '../../contexts/AuthContext';
import { useBaseListOrders } from '../../hooks/useBaseListOrders';
import { SeparationType } from '../../enums/SeparationType';
import { ProfileType } from '../../enums/ProfileType';
import { OrderForListing } from '../../models/OrderForListing';
import { If } from '../../common-components/If/If';
import { BadgeFinancialStatus } from '../../common-components/BadgeFinancialStatus/BadgeFinancialStatus';
import { IWidthColumnOrdersTable, useOrdersTable } from './useOrdersTable';
import { OrderAlertPopover } from './OrderAlertPopover';
import { OrderPopover } from './OrderPopover';
import { RecipeTableData } from './RecipeTableData';
import { CustomerIdentificationCell } from './CustomerIdentificationCell';
import { OrderOptionsCell } from './OrderOptionsCell';
import { StatePrescriptionTableData } from './StatePrescriptionTableData';
import { StateOrderCell } from './StateOrderCell';
import { ClientDeadlineCell } from './ClientDeadlineCell';
import { ExtraRow } from './ExtraRow/ExtraRow';
import { LensMaterialPopover } from './LensMaterialPopover';
import { OrderFulfillmentPopover } from './OrderFulfillmentPopover';
import { TableData } from './styles';

interface IOrdersTableRowProps {
  separationType: SeparationType;
  orderForListing: OrderForListing;
  widthColumn: IWidthColumnOrdersTable;
  visibleColumns?: number;
  userProfile?: ProfileType;
  selectOrder?: (orderId: number) => void;
  handlePrescriptionOption: (orderForListing: OrderForListing, key: any) => void;
  handleGeneralOption: (orderForListing: OrderForListing, key: any) => void;
  changeState: (orderForListing: OrderForListing, id: any) => void;
  statePrescriptionChanged?: (newStateId?: number) => void;
  stateOrderChanged?: (newStateId?: number) => void;
  refresh?: (orderId?: number) => void;
  expressOrder?: boolean;
}

const OrdersTableRow: React.FC<IOrdersTableRowProps> = ({
  separationType,
  orderForListing,
  widthColumn,
  visibleColumns,
  userProfile,
  selectOrder,
  handlePrescriptionOption,
  handleGeneralOption,
  changeState,
  statePrescriptionChanged,
  stateOrderChanged,
  refresh,
  expressOrder
}) => {

  const { session } = useAuth();
  const { getExtraElementsInfo } = useOrdersTable();
  const {
    listedOrderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected,
    listedOrderCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin
  } = useBaseListOrders();

  const orderId = orderForListing.order.id;
  const currentState = orderForListing.stateLog;
  const currentStatePrescription = orderForListing.statePrescriptionLog;
  let backgroundColor = '#fff';

  const workingDaysToShip = orderForListing.order.workingDaysToShip !== undefined
    && orderForListing.order.workingDaysToShip !== null
    ? orderForListing.order.workingDaysToShip
    : '?';

  const clientDelayDaysSinceBaseDateForDeliveryDeadline = orderForListing.clientDelayDaysSinceBaseDateForDeliveryDeadline !== undefined
    && orderForListing.clientDelayDaysSinceBaseDateForDeliveryDeadline !== null
    ? orderForListing.clientDelayDaysSinceBaseDateForDeliveryDeadline
    : '?';
  const shippingDeliveryTimeNumber = orderForListing.order.shippingDeliveryTimeNumber !== undefined
    && orderForListing.order.shippingDeliveryTimeNumber !== null
    ? orderForListing.order.shippingDeliveryTimeNumber
    : '?';

  const internalDelayDaysSinceBaseDateForDeliveryDeadline = orderForListing.internalDelayDaysSinceBaseDateForDeliveryDeadline !== undefined
    && orderForListing.internalDelayDaysSinceBaseDateForDeliveryDeadline !== null
    ? orderForListing.internalDelayDaysSinceBaseDateForDeliveryDeadline
    : '?';
  const internalShippingDeliveryTimeNumber = orderForListing.order.internalShippingDeliveryTimeNumber !== undefined
    && orderForListing.order.internalShippingDeliveryTimeNumber !== null
    ? orderForListing.order.internalShippingDeliveryTimeNumber
    : '?';

  const clientDeadline = clientDelayDaysSinceBaseDateForDeliveryDeadline !== '?' || workingDaysToShip !== '?' || shippingDeliveryTimeNumber !== '?'
    ? clientDelayDaysSinceBaseDateForDeliveryDeadline + '/' + workingDaysToShip + '/' + shippingDeliveryTimeNumber
    : '';
  const internalDeadline = internalDelayDaysSinceBaseDateForDeliveryDeadline !== '?' || workingDaysToShip !== '?' || internalShippingDeliveryTimeNumber !== '?'
    ? internalDelayDaysSinceBaseDateForDeliveryDeadline + '/' + workingDaysToShip + '/' + internalShippingDeliveryTimeNumber
    : '';

  const extraElementsInfo = getExtraElementsInfo(orderForListing);

  const isCrossChannelSalseFulfilledByEcommerceAndWithPhysicalStoreOrigin = listedOrderCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin(orderForListing);
  const isCrossChannelAndCanBeSelected = listedOrderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected(orderForListing);

  backgroundColor = isCrossChannelSalseFulfilledByEcommerceAndWithPhysicalStoreOrigin
    ? '#f8f9fa'
    : backgroundColor;

  return (
    <>
      <tr
        key={`order-${orderId}`}
        data-order-number={orderForListing.order.number}
        data-order-id={orderForListing.order.id}
        data-shopify-id={orderForListing.order.shopifyId}
        className={
          (orderForListing.influencerOrder ? ' influencer-order ' : '') +
          (extraElementsInfo.display ? ' noborderbottom ' : '') +
          (orderForListing._preSaleSport ? ` ${orderTags.pre_sale_sport.css.className} ` : '') +
          (orderForListing._externallyChanged ? ` ${orderTags.externally_changed.css.className} ` : '')
        }
      >
        <TableData width={widthColumn.CheckBox} backgroundColor={backgroundColor}>
          {!orderForListing?.order?.isCrossChannelSale || isCrossChannelAndCanBeSelected ? (
            <Form.Check
              checked={orderForListing._selected === true}
              onChange={() => selectOrder ? selectOrder(orderId) : null}
            />
          ) : null}
        </TableData>

        <If condition={widthColumn.FulfilledBy !== '0px'} >
          <TableData width={widthColumn.FulfilledBy} backgroundColor={backgroundColor} textAlign='center'>
            <OrderFulfillmentPopover orderForListing={orderForListing} />
          </TableData>
        </If>

        <TableData width={widthColumn.Alerts} className='nopadding' backgroundColor={backgroundColor}>
          <OrderAlertPopover orderForListing={orderForListing} />
        </TableData>

        <TableData width={widthColumn.OrderNumber} backgroundColor={backgroundColor}>
          {orderForListing.order.isPhysicalStore ? `` : `#`}{orderForListing.order.number}
          <OrderPopover orderForListing={orderForListing} />
        </TableData>

        <TableData width={widthColumn.PaymentAt} backgroundColor={backgroundColor}>
          {orderForListing.order.paymentAuthorizedAt
            ? dateformat(orderForListing.order.paymentAuthorizedAt, 'dd/mm/yyyy')
            : <BadgeFinancialStatus financialStatus={orderForListing.order.financialStatus} />
          }
        </TableData>

        <RecipeTableData
          separationType={separationType}
          widthColumn={widthColumn}
          orderForListing={orderForListing}
          backgroundColor={backgroundColor}
        />

        <If condition={widthColumn.WorkingDays !== '0px'} >
          <TableData width={widthColumn.WorkingDays} backgroundColor={backgroundColor}>
            {orderForListing._workingDays}
          </TableData>
        </If>

        <If condition={widthColumn.ClientDeadline !== '0px'} >
          <TableData width={widthColumn.ClientDeadline} backgroundColor={backgroundColor} fontSize='0.9em' textAlign='center'>
            <ClientDeadlineCell
              clientDeadline={clientDeadline}
              orderForListing={orderForListing}
            />
          </TableData>
        </If>

        <If condition={widthColumn.InternalDeadline !== '0px'} >
          <TableData width={widthColumn.InternalDeadline} backgroundColor={backgroundColor} fontSize='0.9em' textAlign='center'>
            {internalDeadline}
          </TableData>
        </If>

        <TableData width={widthColumn.ClientName} backgroundColor={backgroundColor}>
          <CustomerIdentificationCell
            orderForListing={orderForListing}
          />
        </TableData>

        <If condition={widthColumn.StateOrder !== '0px'} >
          <TableData width={widthColumn.StateOrder} backgroundColor={backgroundColor}>
            <StateOrderCell
              separationType={separationType}
              currentState={currentState}
              currentStatePrescription={currentStatePrescription}
              orderForListing={orderForListing}
            />
          </TableData>
        </If>

        <StatePrescriptionTableData
          separationType={separationType}
          widthColumn={widthColumn}
          orderForListing={orderForListing}
          backgroundColor={backgroundColor}
        />

        {session?.user?.profileType == ProfileType.Admin || session?.user?.profileType == ProfileType.Ecommerce ? (
          <TableData width={widthColumn.LensMaterial} backgroundColor={backgroundColor} fontSize='0.8em'>
            {orderForListing.lensMaterialName}
            <LensMaterialPopover orderForListing={orderForListing} />
          </TableData>
        ) : null}

        <TableData width={widthColumn.Options} textAlign='right' backgroundColor={backgroundColor}>
          <OrderOptionsCell
            separationType={separationType}
            userProfile={userProfile}
            currentState={currentState}
            currentStatePrescription={currentStatePrescription}
            orderForListing={orderForListing}
            changeState={changeState}
            handleGeneralOption={handleGeneralOption}
            handlePrescriptionOption={handlePrescriptionOption}
            statePrescriptionChanged={statePrescriptionChanged}
            stateOrderChanged={stateOrderChanged}
            refresh={refresh}
          />
        </TableData>
      </tr>
      <ExtraRow extraElementsInfo={extraElementsInfo} orderForListing={orderForListing} colSpan={visibleColumns} />
    </>
  );
}

export { OrdersTableRow };
